import React, { useState, useEffect } from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Typography from "@mui/material/Typography";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import IconButton from "@mui/material/IconButton";
import { useTranslation } from "react-i18next";
import Grid from "@mui/material/Grid";
import { TextField } from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { InputAdornment } from "@mui/material";
import { useDispatch } from "react-redux";
import { requestChangePassword } from "../../redux/actions/userActionCreators";
import { useMediaQuery } from "@mui/material";
import ControlPassword from "../../components/ControlPassword";

export default function AlertDialog({ open, handleClose }) {
  useEffect(() => {
    if (open) {
      setTimeout(() => {
        const changeConfirmNewPassword = document.getElementById(
          "change-confirm-new-password"
        );
        changeConfirmNewPassword.onpaste = (e) => e.preventDefault();
      }, 2000);
    }
  }, [open]);

  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showControl, setShowControl] = useState(false);

  const handleClickShowOldPassword = () => {
    setShowOldPassword(!showOldPassword);
  };

  const handleClickShowNewPassword = () => {
    setShowNewPassword(!showNewPassword);
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const formik = useFormik({
    initialValues: {
      oldPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
    enableReinitialize: true,
    validationSchema: Yup.object({
      oldPassword: Yup.string()
        .max(255)
        .required(t("required_field"))
        .matches(
          /^(?=.{8,})(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[@#$%^&+=*?!-]).*$/,
          t("password_policy")
        ),
      newPassword: Yup.string()
        .max(255)
        .required(t("required_field"))
        .matches(
          /^(?=.{8,})(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[@#$%^&+=*?!-]).*$/,
          t("password_policy")
        ),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref("newPassword"), null], t("match_password"))
        .required(t("required_field")),
    }),
    onSubmit: (values, { resetForm }) => {
      dispatch(
        requestChangePassword(
          values.oldPassword,
          values.newPassword,
          values.confirmPassword
        )
      );
      resetForm({ values: "" });
      handleClose();
    },
  });
  const isMobile = useMediaQuery("(max-width:1400px)");
  return (
    <div>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="xl"
        PaperProps={{
          sx: !isMobile
            ? { width: "35%", padding: "20px " }
            : { width: "90%", margin: "5px 0", padding: "5px " },
        }}
      >
        <Typography
          variant="subtitle3"
          sx={{ display: "flex", alignItems: "center", mb: 1 }}
        >
          <IconButton onClick={handleClose}>
            <KeyboardBackspaceIcon sx={{ width: "70%", color: "black" }} />
          </IconButton>
          {t("back")}
        </Typography>
        <form onSubmit={formik.handleSubmit}>
          <DialogTitle>
            <Typography variant="title4">{t("change_password")}</Typography>
          </DialogTitle>
          <DialogContent>
            <Grid container mt={2} spacing={3}>
              <Grid item lg={12} xs={12}>
                <Typography variant="subtitle2" mb={2}>
                  {t("old_password")}
                </Typography>

                <TextField
                  id="change-old-password"
                  placeholder={t("enter_old_password")}
                  name="oldPassword"
                  variant="outlined"
                  fullWidth
                  error={Boolean(
                    formik.touched.oldPassword && formik.errors.oldPassword
                  )}
                  helperText={
                    formik.touched.oldPassword && formik.errors.oldPassword
                  }
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  value={formik.values.oldPassword}
                  type={showOldPassword ? "text" : "password"}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleClickShowOldPassword}
                          onMouseDown={handleMouseDownPassword}
                          edge="end"
                          size="large"
                        >
                          {showOldPassword ? <Visibility /> : <VisibilityOff />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item lg={12} xs={12}>
                <Typography variant="subtitle2" mb={2}>
                  {t("new_password")}
                </Typography>
                <TextField
                  id="change-new-password"
                  placeholder={t("enter_new_password")}
                  name="newPassword"
                  variant="outlined"
                  fullWidth
                  error={Boolean(
                    formik.touched.newPassword && formik.errors.newPassword
                  )}
                  helperText={
                    formik.touched.newPassword && formik.errors.newPassword
                  }
                  onBlur={(e) => {
                    formik.handleBlur(e);
                    setShowControl(false);
                  }}
                  onFocus={() => setShowControl(true)}
                  onChange={formik.handleChange}
                  value={formik.values.newPassword}
                  type={showNewPassword ? "text" : "password"}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleClickShowNewPassword}
                          onMouseDown={handleMouseDownPassword}
                          edge="end"
                          size="large"
                        >
                          {showNewPassword ? <Visibility /> : <VisibilityOff />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              {showControl && (
                <Grid item xs={12} sx={{ marginTop: "-25px" }}>
                  <ControlPassword value={formik.values.newPassword} />
                </Grid>
              )}

              <Grid item xs={12}>
                <Typography variant="subtitle2" mb={2}>
                  {t("confirm_new_password")}
                </Typography>
                <TextField
                  id="change-confirm-new-password"
                  placeholder={t("enter_confirm_new_password")}
                  variant="outlined"
                  name="confirmPassword"
                  type="password"
                  fullWidth
                  error={Boolean(
                    formik.touched.confirmPassword &&
                      formik.errors.confirmPassword
                  )}
                  helperText={
                    formik.touched.confirmPassword &&
                    formik.errors.confirmPassword
                  }
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  value={formik.values.confirmPassword}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button
              variant="contained"
              color="button"
              type="submit"
              fullWidth
              sx={{ margin: "15px 15px" }}
            >
              {t("edit")}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </div>
  );
}
